import { useFormik } from 'formik';
import {
  AutopopulateAccommodationPlacesScenario,
  CreateEventMutation,
  CreateEventPayload0301,
  EventType,
  EventUserStatus,
  useTrackImpactRadiusMutation,
  useUpdateTravelSettingsMutation,
} from '@graphql/generated';
import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import * as Yup from 'yup';
import { getLocalStorage, useHistory, useIdentify, useSegmentGroupCallForEvent, useTranslation } from '@shared/core';
import { useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { mapCurrencyCodeToTravelCurrencyCode } from '@shared/utils/currency/currencyMapper';
import { calculateStayDates, getLocaleDateString } from '@shared/utils/dateHelpers';
import { useEffect, useMemo, useState } from 'react';
import { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { addMonths } from 'date-fns';
import { useDesignParams } from '@apps/createWedding/hooks/useDesignParams';
import { getTelemetryDateLabel } from '@apps/createWedding/utils';
import { useFieldErrors } from '@shared/hooks/useFieldErrors';
import { useCreateWeddingTelemetry } from '@apps/createWedding/CreateWedding.telemetry';
import { createAndAttachPixelImage } from '@apps/createWedding/components/PixelImage/PixelImage';
import { getTimezone } from '@apps/createWedding/utils/getTimezone';
import { CREATE_WEDDING_APP_NAME } from '@apps/createWedding/constants';
import { useViewContext } from '@apps/createWedding/components/View/ViewProvider';
import { useEventState } from '@apps/createWedding/EventState';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { readAcquisitionContexts, readAndResetAcquisitionContexts } from '@shared/createEvent/acquisitionContextTracking';
import { StoredAcquisitionContextDetails } from '@shared/createEvent/acquisitionContextTracking/acquisitionContextTracking.types';
import { addAction, addError } from '@shared/utils/logger';
import { getEventTypeConfig } from '@config';
import { getCreateEventPayload } from '@shared/utils/impactRadius/utilsImpactRadius';
import { useCreateEventFeatures, useDefaultTemplatePayload, useWebsiteSettingsFromQueryParam } from './hooks';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useDisclosure } from '@withjoy/joykit';
import { useAutoPopulation as useAccommodationsAutoPopulation } from '@shared/utils/accommodations';
import { useStayAreasAutoPopulation } from '@shared/utils/stayAreas/useStayAreasAutoPopulation';
import { useEventPlaceActions } from '@shared/utils/eventPlace';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { getValidCoordinates } from '@shared/core/places';
import type { UpdateTravelSettingsRequest } from '@graphql/generated';
import { recordCreateWeddingFlowEvent } from '@apps/createWedding/createWeddingFlowEvents';

const CREATE_WEDDING_KEY = '@@createwedding';

export const PENDING_CREATION_KEY = '@@createwedding/pending-creation';

export interface EventVenueLocation
  extends Readonly<{
    placeId?: string;
    locationValue?: string;
    displayName?: string;
    address?: string;
    city?: string;
    latitude?: number;
    longitude?: number;
  }> {}

export type CreateWeddingFormFields = Readonly<{
  ownerFirstName: string;
  partnerFirstName: string | null;
  ownerLastName: string | null;
  weddingDate: Date | null;
  isDateValid: boolean;
  location: Location | null;
  eventVenue: EventVenueLocation | null;
  font?: string;
  template?: string;
  scenario?: string;
  theme?: string;
  locale: string;
}>;

export type OnEventCreateSuccessHandler = (data: NonNullable<CreateEventMutation['createEventV0301']>) => void;

export interface CreateWeddingFormProps
  extends Readonly<{
    showQuestionnaire?: boolean;
    skipWaitForDesignParamToSettle?: boolean;
    onAfterEventCreateInitiated?: () => void;
    // The success of the event is handled in different ways depending on the flow
    onEventCreateSuccess?: OnEventCreateSuccessHandler;
    isCreateCtaLoading?: boolean;
    logoutRedirectUri?: string;
  }> {}

interface UseCreateWeddingFormControllerArgs extends CreateWeddingFormProps {}

export const useCreateWeddingFormController = (args: UseCreateWeddingFormControllerArgs) => {
  const { showQuestionnaire = true, logoutRedirectUri, onAfterEventCreateInitiated, onEventCreateSuccess, skipWaitForDesignParamToSettle } = args;
  const authProvider = useAuth();
  const { identify } = useIdentify();
  const viewContext = useViewContext(false);
  const { createEvent, addEventCreatedListener } = useEventState();
  const localStorage = getLocalStorage();
  const { tq } = useTranslation('createWedding');
  const { designParams, settled } = useDesignParams();
  const telemetry = useCreateWeddingTelemetry();
  const { eventMemberships } = useEventMemberships();
  const [blockWeddingDate, setBlockWeddingDate] = useState<boolean>(false);
  const [blockEventVenue, setBlockEventVenue] = useState<boolean>(false);
  const [trackImpactRadius] = useTrackImpactRadiusMutation();
  const enableBlankDates = useFeatureValue('enableBlankDates');
  const { getWebsiteDesignSettings, getWebsiteHeaderLayout } = useWebsiteSettingsFromQueryParam();
  const applyDefaultTemplatePayload = useDefaultTemplatePayload();
  const queryParamHelper = useQueryParamHelper();
  const customEventQueryParam = queryParamHelper.getValueString('customEvent');
  const { isOpen: isCustomEventDialogOpen, onClose: onCloseCustomEventDialog, onOpen: onOpenCustomEventDialog } = useDisclosure();
  const [isCreateWeddingHeaderLayoutExperimentEnabled] = useIsFeatureEnabled('createWeddingHeaderLayoutExperiment');
  const [isAccommodationsStayAreasEnabled] = useIsFeatureEnabled('accommodationsStayAreas');
  const history = useHistory();

  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();
  const [updateTravelSettings] = useUpdateTravelSettingsMutation();
  // Preserve full region-specific locale (e.g., es-ES, fr-FR, it-IT) for server.
  // i18n and date formatting systems will normalize these when interpreting.
  const { currency, languageCodeIso15897: eventLocale } = useGeoBasedLocale();

  const { getEventFeatures } = useCreateEventFeatures();

  const { startAutoPopulation: startAccommodationsAutoPopulation } = useAccommodationsAutoPopulation({
    mode: 'start-only',
    context: 'createWedding',
  });

  const { startAutoPopulation: startStayAreasAutoPopulation } = useStayAreasAutoPopulation({
    mode: 'start-only',
    context: 'createWedding',
  });

  const { createEventPlace } = useEventPlaceActions({
    onCompleted: () => {},
    onError: () => {},
  });

  const withAutoPopulationErrorLogging =
    <T,>(operation: string, eventId: string) =>
    async (promise: Promise<T>): Promise<T> => {
      try {
        return await promise;
      } catch (error) {
        addError(error, {
          context: 'CreateWedding/autoPopulation',
          eventId,
          operation,
        });
        throw error;
      }
    };

  // Auto populate accommodations
  const autoPopulateAccommodations = useEventCallback(async (eventId: string, latitude?: number, longitude?: number, placeId?: string) => {
    await startAccommodationsAutoPopulation({
      venue: { latitude, longitude, googlePlaceId: placeId },
      eventId,
      scenario: AutopopulateAccommodationPlacesScenario.eventCreate,
    });
  });

  // Auto populate stay areas
  const autoPopulateStayAreas = useEventCallback(async (eventId: string, latitude?: number, longitude?: number) => {
    await startStayAreasAutoPopulation({
      venue: getValidCoordinates({ latitude, longitude }),
      eventId,
    });
  });

  const waitForDesignDeferred = useMemo(() => {
    let outsideResolve: (value?: unknown) => void = () => {};
    const promise = new Promise(resolve => {
      outsideResolve = resolve;
    });
    return { promise, resolve: outsideResolve };
  }, []);

  const shouldResolveDesignPromise = settled || skipWaitForDesignParamToSettle;
  const headerLayout = useMemo(
    () => (isCreateWeddingHeaderLayoutExperimentEnabled ? getWebsiteHeaderLayout() : null),
    [getWebsiteHeaderLayout, isCreateWeddingHeaderLayoutExperimentEnabled],
  );
  useEffect(() => {
    if (shouldResolveDesignPromise) {
      waitForDesignDeferred.resolve();
    }
  }, [shouldResolveDesignPromise, waitForDesignDeferred]);

  const initialValuesFromLocalStorage = useMemo(() => {
    const values = localStorage.getItem(CREATE_WEDDING_KEY);

    if (!values) {
      return;
    }

    const parsedVal = JSON.parse(values);

    return { ...parsedVal, ...(parsedVal.weddingDate ? { weddingDate: new Date(parsedVal.weddingDate) } : {}) };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTrackImpactRadius = async (event: CreateEventMutation) => {
    try {
      const eventTypeConfig = getEventTypeConfig(EventType.wedding);
      const payload = getCreateEventPayload(
        eventTypeConfig.impactRadiusCampaignId,
        eventTypeConfig.impactRadiusCreateEventActionTrackerId,
        event.createEventV0301?.id,
        authProvider.currentUser.profile?.activeAlias?.auth0Id,
      );

      if (!payload) {
        return;
      }

      await trackImpactRadius({ variables: { payload } });
    } catch (e) {
      telemetry.trackError('CreateWeddingTrackImpactRadius', e);
    }
  };

  const handleCreateEvent = async (values: CreateWeddingFormFields) => {
    const { timezoneId, timezoneMethod } = getTimezone();
    const eventDate = blockWeddingDate ? null : values.weddingDate;
    const eventDateToIsoString = eventDate?.toISOString() || null;
    const durationDays = eventDate ? Math.ceil((eventDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : undefined;

    telemetry.dateSet(eventDateToIsoString ?? 'dateUndecided', getTelemetryDateLabel(eventDate), durationDays);
    telemetry.timezoneRegistered({ timezoneId, timezoneMethod });
    telemetry.submitDetailsClicked(authProvider.currentUser?.profile?.email ?? '');
    recordCreateWeddingFlowEvent({
      action: 'FormSubmitted',
      venueProvided: !blockEventVenue && !!values.eventVenue?.placeId,
      dateProvided: !blockWeddingDate && !!eventDate,
      lastNameProvided: !!values.ownerLastName,
      durationDays: durationDays ?? null,
    });
    const acquisitionContext = readAcquisitionContexts();
    let forceHotelBlockEnabled = false;
    const checkAcquisitionContextData = (dataDetails: StoredAcquisitionContextDetails, key: string, value: string) => {
      for (const item of dataDetails.data) {
        if (item.key === key && item.value === value) {
          return true;
        }
      }
      return false;
    };

    acquisitionContext.forEach(item => {
      if (item?.relativeUrl?.includes('hotel-room-blocks') || checkAcquisitionContextData(item, 'utm_campaign', '[SAGE]-Wedding-Website-New-MarComm-Travel-Segments-Only')) {
        forceHotelBlockEnabled = true;
      }
    });

    addAction('checkForceHotelBlockEnabled', { acquisitionContext, forceHotelBlockEnabled, userId: authProvider.currentUser?.profile?.id });

    await waitForDesignDeferred.promise;

    const eventLocation = blockEventVenue ? {} : { lat: values.location?.lat, lng: values.location?.lng, placeId: values.location?.placeId, placeName: values.location?.place };
    let payload: CreateEventPayload0301 = {
      ownerFirstName: values.ownerFirstName,
      fianceeFirstName: values.partnerFirstName,
      ownerLastName: values.ownerLastName,
      fianceeLastName: '',
      font: values.font,
      template: values.template,
      mainTzid: timezoneId,
      theme: values.theme,
      mainDate: enableBlankDates ? eventDateToIsoString : eventDateToIsoString || addMonths(new Date(), 9).toISOString(),
      acquisitionContext: {
        currentTimestamp: Date.now(),
        details: readAndResetAcquisitionContexts(),
      },
      eventFeatures: [...getEventFeatures()],
      // Preserve full region-specific locale (e.g., es-ES, fr-FR, it-IT) for server.
      // i18n and date formatting systems will normalize these when interpreting.
      locale: values.locale,
      ...eventLocation,
    };

    const websiteDesignSettings = getWebsiteDesignSettings();

    if (websiteDesignSettings) {
      payload.websiteDesignSettings = websiteDesignSettings;
    }

    if (isCreateWeddingHeaderLayoutExperimentEnabled && headerLayout) {
      payload.headerLayout = headerLayout;
    }

    if (!websiteDesignSettings && !headerLayout) {
      const { payload: defaultTemplatePayload, error } = await applyDefaultTemplatePayload(payload);
      if (error) {
        formik.setSubmitting(false);
        return;
      }
      payload = defaultTemplatePayload;
    }
    createEvent({
      variables: {
        payload,
      },
    });
    localStorage.setItem(CREATE_WEDDING_KEY, undefined);
    createAndAttachPixelImage({ action: 'lead' });
    addEventCreatedListener(async event => {
      if (event.createEventV0301) {
        const { id: eventId, firebaseId, website, info } = event.createEventV0301!;

        executeSegmentGroupCallForEvent(firebaseId, {
          postgresEventId: eventId,
          eventType: info.eventType,
        });
        const adminOrOwnerEventCount = eventMemberships?.filter(x => x.status === EventUserStatus.admin || x.status === EventUserStatus.owner).length;
        telemetry.weddingCreated({
          website,
          eventId: firebaseId,
          email: authProvider.currentUser.profile?.email ?? '',
          adminOrOwnerEventCount,
          venueLocationProvided: !blockEventVenue && !!values.eventVenue?.placeId,
          venueLocationSkipped: blockEventVenue,
          lastNameProvided: !!values.ownerLastName,
        });
        recordCreateWeddingFlowEvent({
          action: 'EventCreated',
          eventId: firebaseId,
          website,
          adminOrOwnerEventCount,
          venueProvided: !blockEventVenue && !!values.eventVenue?.placeId,
          dateProvided: !blockWeddingDate && !!eventDate,
          lastNameProvided: !!values.ownerLastName,
          durationDays: durationDays ?? null,
        });
        handleTrackImpactRadius(event);

        try {
          const payload: UpdateTravelSettingsRequest = {
            eventId,
            currencyCode: mapCurrencyCodeToTravelCurrencyCode(currency),
          };

          if (eventDate) {
            const stayDates = calculateStayDates(eventDate.toISOString());
            payload.checkInDate = getLocaleDateString(stayDates.checkIn.date);
            payload.checkOutDate = getLocaleDateString(stayDates.checkOut.date);
          }

          await updateTravelSettings({
            variables: { payload },
          });
        } catch (error) {
          addError(error, { context: 'CreateWedding/updateTravelSettings', eventId, currency });
        }

        if (!blockEventVenue && values.eventVenue?.placeId) {
          await createEventPlace({
            payload: {
              googlePlaceId: values.eventVenue.placeId,
            },
            eventId,
          });
          const wrapError = withAutoPopulationErrorLogging;
          const autoPopulationPromises = [
            wrapError('accommodations', eventId)(autoPopulateAccommodations(eventId, values.eventVenue.latitude, values.eventVenue.longitude, values.eventVenue.placeId)),
            isAccommodationsStayAreasEnabled && wrapError('stayAreas', eventId)(autoPopulateStayAreas(eventId, values.eventVenue.latitude, values.eventVenue.longitude)),
          ].filter(Boolean);
          await Promise.allSettled(autoPopulationPromises);
        }

        identify({ forceHotelBlockEnabled });

        onEventCreateSuccess?.(event.createEventV0301);
      }
    });

    onAfterEventCreateInitiated?.();

    if (showQuestionnaire) {
      viewContext?.changeView('questionnaire');
    } else if (!onEventCreateSuccess) {
      history.push(`/${CREATE_WEDDING_APP_NAME}/tying-the-knot`);
    }
  };

  const formik = useFormik<CreateWeddingFormFields>({
    initialValues: {
      ownerFirstName: '',
      ownerLastName: '',
      partnerFirstName: '',
      weddingDate: addMonths(new Date(), 9),
      location: null,
      eventVenue: null,
      ...initialValuesFromLocalStorage,
      ...designParams,
      isDateValid: true,
      locale: eventLocale,
    },
    validationSchema: Yup.object<CreateWeddingFormFields>({
      ownerFirstName: Yup.string().required(tq`We need your first name.`),
      ownerLastName: Yup.string().nullable(),
      partnerFirstName: Yup.string().nullable(),
      weddingDate: Yup.date()
        .nullable()
        .test('valid date', tq`Please re-enter the date in MM/DD/YYYY format`, function () {
          const isDateValid = this.parent.isDateValid;
          return blockWeddingDate || isDateValid;
        }),
      isDateValid: Yup.boolean(),
      location: (
        Yup.object<Location>({
          place: Yup.string(),
          lng: Yup.number(),
          lat: Yup.number(),
          placeId: Yup.string(),
        }) as Yup.ObjectSchema<Location>
      ).nullable(true),

      eventVenue: (
        Yup.object<EventVenueLocation>({
          locationValue: Yup.string().when([], {
            is: () => blockEventVenue,
            then: Yup.string().notRequired(),
            otherwise: Yup.string().required(tq`You can add a venue you’re considering and update this later — or check the box to skip for now.`),
          }),
          latitude: Yup.number(),
          longitude: Yup.number(),
          placeId: Yup.string(),
        }) as Yup.ObjectSchema<EventVenueLocation>
      )
        .nullable(true)
        .when([], {
          is: () => !blockEventVenue,
          then: (schema: Yup.ObjectSchema<EventVenueLocation>) =>
            schema.required(tq`You can add a venue you’re considering and update this later — or check the box to skip for now.`),
          otherwise: (schema: Yup.ObjectSchema<EventVenueLocation>) => schema.notRequired(),
        }),
      font: Yup.string(),
      template: Yup.string(),
      scenario: Yup.string(),
      theme: Yup.string(),
      locale: Yup.string().required(),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, formikHelpers) => {
      formikHelpers.setFieldTouched('eventVenue', true, false);
      handleCreateEvent(values);
    },
  });

  useEffect(() => {
    if (customEventQueryParam?.length) {
      onOpenCustomEventDialog();
      formik.setFieldValue('ownerFirstName', customEventQueryParam || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customEventQueryParam, onOpenCustomEventDialog]);

  const getFieldErrorProps = useFieldErrors<CreateWeddingFormFields>(formik);

  useEffect(() => {
    localStorage.setItem(CREATE_WEDDING_KEY, JSON.stringify(formik.values));
  }, [formik.values]); // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = () => authProvider.provideLogout({}, logoutRedirectUri ?? `${window.location.origin}/${CREATE_WEDDING_APP_NAME}`);

  return {
    formik,
    user: authProvider.currentUser.profile!,
    getFieldErrorProps,
    signOut,
    blockWeddingDate,
    setBlockWeddingDate,
    blockEventVenue,
    setBlockEventVenue,
    telemetry,
    isCustomEventDialogOpen,
    onCloseCustomEventDialog,
  };
};
